import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import {
  extractVideoUrl,
  extractThumbnail,
  extractTitle,
  extractIsNsfw,
  formatTimeAgo,
} from '@/hooks/useVideoFeed';
import { useVideoThumbnail } from '@/hooks/useVideoThumbnail';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { YouTubeEmbed } from '@/components/YouTubeEmbed';
import { isYouTubeUrl, extractYouTubeVideoId, getYouTubeThumbnailUrl } from '@/lib/youtube';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/* ── Header height constant — must match Layout ── */
const HEADER_H = 56; // px  (matches the `h-[56px]` in Layout.tsx)
const NAV_H = 56;    // px  (matches MobileNav height)

/* ── Reaction query for a single slide ─────────────────────────────────── */
function useSlideReactions(event: NostrEvent) {
  const { nostr } = useNostr();
  const isAddressable = event.kind >= 30000 && event.kind < 40000;
  return useQuery({
    queryKey: ['slide-reactions', event.id],
    queryFn: async (ctx) => {
      if (isAddressable) {
        const dTag = event.tags.find(([t]) => t === 'd')?.[1] ?? '';
        const coord = `${event.kind}:${event.pubkey}:${dTag}`;
        const [byId, byCoord] = await Promise.all([
          nostr.query([{ kinds: [7], '#e': [event.id], limit: 200 }], { signal: ctx.signal }),
          nostr.query([{ kinds: [7], '#a': [coord], limit: 200 }], { signal: ctx.signal }),
        ]);
        const seen = new Set<string>();
        return [...byId, ...byCoord].filter(e => {
          if (seen.has(e.id)) return false;
          seen.add(e.id);
          return true;
        });
      }
      return nostr.query([{ kinds: [7], '#e': [event.id], limit: 200 }], { signal: ctx.signal });
    },
    staleTime: 30000,
  });
}

const POSITIVE_REACTIONS = new Set(['+', '❤️', '🤙', '👍', '🔥', '✨', '😍', '🙌']);
const isPositive = (content: string) => content === '+' || POSITIVE_REACTIONS.has(content);

/* ── Single short slide ─────────────────────────────────────────────────── */
interface ShortSlideProps {
  event: NostrEvent;
  isActive: boolean;
}

function ShortSlide({ event, isActive }: ShortSlideProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;
  const [nsfwRevealed, setNsfwRevealed] = useState(false);
  const [muted, setMuted] = useState(false);

  const { user } = useCurrentUser();
  const { mutate: publishEvent } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: reactions, refetch: refetchReactions } = useSlideReactions(event);

  const title = extractTitle(event);
  const storedThumbnail = extractThumbnail(event);
  const videoUrl = extractVideoUrl(event);
  const isNsfw = extractIsNsfw(event);
  const timeAgo = formatTimeAgo(event.created_at);
  const displayName = metadata?.name ?? metadata?.display_name ?? event.pubkey.slice(0, 8) + '…';

  // YouTube bridge support
  const isYtVideo = videoUrl ? isYouTubeUrl(videoUrl) : false;
  const ytVideoId = isYtVideo && videoUrl ? extractYouTubeVideoId(videoUrl) : null;
  const ytFallbackThumb = ytVideoId ? getYouTubeThumbnailUrl(ytVideoId) : undefined;

  const thumbnail = useVideoThumbnail(event.id, storedThumbnail ?? ytFallbackThumb, isYtVideo ? undefined : videoUrl);

  // Like state
  const likesCount = reactions?.filter(r => isPositive(r.content)).length ?? 0;
  const alreadyLiked = !!user && !!reactions?.some(r => r.pubkey === user.pubkey && isPositive(r.content));

  const handleLike = () => {
    if (!user || alreadyLiked) return;
    const isAddressable = event.kind >= 30000 && event.kind < 40000;
    const dTag = event.tags.find(([t]) => t === 'd')?.[1] ?? '';
    const tags: string[][] = [
      ['e', event.id],
      ['p', event.pubkey],
      ['k', String(event.kind)],
    ];
    if (isAddressable) tags.push(['a', `${event.kind}:${event.pubkey}:${dTag}`]);
    publishEvent(
      { kind: 7, content: '👍', tags },
      {
        onSuccess: () => {
          toast({ title: 'Liked!' });
          queryClient.invalidateQueries({ queryKey: ['slide-reactions', event.id] });
          refetchReactions();
        },
      },
    );
  };

  // Auto-play/pause based on visibility
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || isYtVideo) return;
    if (isActive) {
      vid.currentTime = 0;
      vid.play().catch(() => {
        vid.muted = true;
        setMuted(true);
        vid.play().catch(() => undefined);
      });
    } else {
      vid.pause();
    }
  }, [isActive, isYtVideo]);

  const toggleMute = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = !vid.muted;
    setMuted(vid.muted);
  };

  return (
    <div
      className="relative w-full shrink-0 snap-start snap-always overflow-hidden bg-black"
      style={{ height: `calc(100dvh - ${HEADER_H}px - ${NAV_H}px)` }}
    >
      {/* Video / NSFW gate */}
      {isNsfw && !nsfwRevealed ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 z-10">
          {thumbnail && (
            <img
              src={thumbnail}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              style={{ filter: 'blur(24px)', transform: 'scale(1.1)' }}
            />
          )}
          <div className="relative flex flex-col items-center gap-3">
            <span className="text-5xl">🔞</span>
            <p className="text-white font-bold text-lg">Sensitive Content</p>
            <button
              onClick={() => setNsfwRevealed(true)}
              className="px-6 py-2.5 rounded-full text-sm font-bold text-white"
              style={{ background: 'var(--gb-accent)' }}
            >
              Tap to reveal
            </button>
          </div>
        </div>
      ) : isYtVideo && ytVideoId ? (
        /* YouTube bridge — fullscreen iframe */
        <div className="absolute inset-0 flex flex-col bg-black">
          <div className="flex-1 relative">
            <YouTubeEmbed videoId={ytVideoId} originalUrl={videoUrl ?? undefined} fillParent />
          </div>
        </div>
      ) : videoUrl ? (
        <video
          ref={videoRef}
          src={videoUrl}
          poster={thumbnail ?? undefined}
          playsInline
          loop
          muted={muted}
          className="absolute inset-0 w-full h-full object-cover"
          preload="metadata"
        />
      ) : (
        /* Placeholder while URL resolves */
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {thumbnail ? (
            <img src={thumbnail} alt={title} className="w-full h-full object-cover" />
          ) : (
            <div className="text-white/40 text-center">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" className="mx-auto mb-2">
                <path d="M8 5v14l11-7z" />
              </svg>
              <p className="text-sm">Loading…</p>
            </div>
          )}
        </div>
      )}

      {/* Bottom gradient + info */}
      <div
        className="absolute inset-x-0 bottom-0 z-20 px-4 pb-4 pt-24"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)' }}
      >
        {/* Author row */}
        <div className="flex items-center gap-2 mb-2">
          <Link to={`/profile/${event.pubkey}`} className="shrink-0">
            <Avatar className="h-9 w-9 border-2 border-white/60">
              <AvatarImage src={metadata?.picture} />
              <AvatarFallback className="text-xs font-bold bg-white/20 text-white">
                {displayName[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </Link>
          <div className="min-w-0">
            <Link
              to={`/profile/${event.pubkey}`}
              className="block text-white font-semibold text-sm truncate no-underline"
            >
              {displayName}
            </Link>
            <p className="text-white/60 text-xs">{timeAgo}</p>
          </div>
        </div>

        {/* Title */}
        <p
          className="text-white text-sm font-medium leading-snug"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          } as React.CSSProperties}
        >
          {title}
        </p>
      </div>

      {/* Right-side controls */}
      <div className="absolute right-3 bottom-20 z-20 flex flex-col items-center gap-4">
        {/* Like button */}
        <button
          onClick={handleLike}
          disabled={!user || alreadyLiked}
          aria-label={alreadyLiked ? 'Liked' : 'Like'}
          className="flex flex-col items-center gap-0.5"
        >
          <div
            className="flex items-center justify-center w-10 h-10 rounded-full"
            style={{
              background: alreadyLiked ? 'rgba(58,158,58,0.5)' : 'rgba(0,0,0,0.4)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill={alreadyLiked ? 'white' : 'none'} stroke="white" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
            </svg>
          </div>
          {likesCount > 0 && (
            <span className="text-white text-xs font-semibold">{likesCount}</span>
          )}
        </button>

        {/* Mute toggle — only for native video */}
        {!isYtVideo && (
          <button
            onClick={toggleMute}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-black/40"
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </button>
        )}

        {/* Full page link */}
        <Link
          to={`/video/${event.id}`}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-black/40"
          aria-label="Open full page"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
            <path d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

/* ── Fullscreen shorts viewer ────────────────────────────────────────────── */
interface ShortsViewerProps {
  events: NostrEvent[];
  initialIndex?: number;
  onClose: () => void;
}

export function ShortsViewer({ events, initialIndex = 0, onClose }: ShortsViewerProps) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrolling = useRef(false);

  // Scroll to initial index without animation on mount
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const slideH = el.clientHeight;
    el.scrollTop = initialIndex * slideH;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track active index from scroll position
  const handleScroll = useCallback(() => {
    if (isScrolling.current) return;
    const el = containerRef.current;
    if (!el) return;
    const slideH = el.clientHeight;
    const newIndex = Math.round(el.scrollTop / slideH);
    if (newIndex !== activeIndex) setActiveIndex(newIndex);
  }, [activeIndex]);

  // Lock body scroll while viewer is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed left-0 right-0 z-[300] bg-black"
      style={{
        top: HEADER_H,
        bottom: NAV_H,
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-3 left-3 z-30 flex items-center justify-center w-9 h-9 rounded-full bg-black/50"
        aria-label="Close shorts"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
          <path d="M19 11H7.83l4.88-4.88c.39-.39.39-1.03 0-1.42-.39-.39-1.02-.39-1.41 0l-6.59 6.59c-.39.39-.39 1.02 0 1.41l6.59 6.59c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L7.83 13H19c.55 0 1-.45 1-1s-.45-1-1-1z" />
        </svg>
      </button>

      {/* Scroll indicator dots */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-1.5">
        {events.map((_, i) => (
          <div
            key={i}
            className="rounded-full transition-all duration-200"
            style={{
              width: 4,
              height: i === activeIndex ? 16 : 4,
              background: i === activeIndex ? 'white' : 'rgba(255,255,255,0.4)',
            }}
          />
        ))}
      </div>

      {/* Vertical scrollable container */}
      <div
        ref={containerRef}
        className="h-full overflow-y-scroll"
        style={{
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        } as React.CSSProperties}
        onScroll={handleScroll}
      >
        {events.map((event, i) => (
          <ShortSlide key={event.id} event={event} isActive={i === activeIndex} />
        ))}
      </div>
    </div>
  );
}
